import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Scrollbars } from 'react-custom-scrollbars';
import { useStore, useStoreMap } from 'effector-react';
import { ImageType } from '@types';
import Image from '@components/Image';
import {
  $GateRotateAudioLength,
  $GateRotateSubtitleLength,
  $rotateCurrentLeft,
  $rotateEditAction,
  $rotateEditApi,
  type RotateEditAction,
} from '@stores/autoRotateStores';
import { $loadedId, $loadedScene } from '@stores/loadedStore';
import { RulerItem } from './RulerItem';
import { PX_PER_SEC } from '../constants';
import { RorateList } from './RorateList';
import { RotateAudio } from './RotateAudio';
import { RotateSubtitle } from './RotateSubtitle';

const ThumbHorizontal = styled.div`
  cursor: pointer;
  background-color: #454545;
  border-radius: 5px;
`;

const TrackHorizontal = styled.div`
  min-width: 100%;
  z-index: 150;
  bottom: 5px;
`;

export function RotateTimeline() {
  const loadedId = useStore($loadedId)!;
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<Scrollbars>(null);
  const timeId = useRef<ReturnType<typeof setTimeout>>();
  const currentLeft = useStore($rotateCurrentLeft);

  const audioLength = useStore($GateRotateAudioLength.state);
  const subTitleLength = useStore($GateRotateSubtitleLength.state);
  const lengthRef = useRef<number>(120);

  const rotateLength = useStoreMap({
    store: $loadedScene,
    keys: [audioLength, subTitleLength],
    fn: (state) => {
      const list = state?.config?.autoRotate?.tourData ?? [];
      const last = list[list.length - 1];
      const length = last ? last.startAt + last.duration : 0;
      const max = Math.ceil(Math.max(length, audioLength, subTitleLength));
      if (max > lengthRef.current) {
        lengthRef.current = max;
      }
      return lengthRef.current;
    },
  });

  const rotateWidth = useMemo(() => rotateLength * PX_PER_SEC, [rotateLength]);
  const count = useMemo(
    () => Array.from(Array(rotateLength + 1).keys()),
    [rotateLength]
  );
  const [trigger, setTrigger] = useState<'left' | 'right' | 'none'>('none');
  const itemActionRef = useRef<Pick<
    RotateEditAction,
    'type' | 'index' | 'blockLeft'
  > | null>(null);
  const mouseOffset = useRef<number>(0);
  const currengLeft = useStore($rotateCurrentLeft);

  useEffect(() => {
    if (trigger === 'right') {
      timeId.current = setInterval(() => {
        const left = scrollRef.current?.getScrollLeft() ?? 0;
        scrollRef.current?.scrollLeft(left + 6);
      }, 10);
    } else if (trigger === 'left') {
      timeId.current = setInterval(() => {
        const left = scrollRef.current?.getScrollLeft() ?? 0;
        scrollRef.current?.scrollLeft(left - 6);
      }, 10);
    } else {
      clearInterval(timeId.current!);
    }
  }, [trigger]);

  useEffect(() => {
    // 鼠标拖动中不执行
    if (trigger !== 'none') return;
    if (!scrollRef.current) return;
    const width = scrollRef.current.getClientWidth();
    const scrollLeft = scrollRef.current.getScrollLeft();
    const autoLeft = scrollLeft + width - 60;
    if (currentLeft < scrollLeft) {
      scrollRef.current.scrollLeft(currentLeft);
    } else if (currentLeft > autoLeft) {
      const target = Math.min(
        Math.max(0, currentLeft - (width - 60)),
        rotateWidth - width + 60
      );
      scrollRef.current.scrollLeft(target);
    }
  }, [currentLeft]);

  const doAction = () => {
    const action = $rotateEditAction.getState();
    if (!action) return;
    const scrollOffset =
      (scrollRef.current?.getScrollLeft() ?? 0) - action.scrollBarLeft;
    const targetLeft = action.blockLeft + (mouseOffset.current + scrollOffset);
    if (action.type === 'moveBlock') {
      $rotateEditApi.moveBlock({
        index: action.index,
        targetLeft,
        sceneId: loadedId,
      });
    } else if (action.type === 'moveLine') {
      $rotateEditApi.setCurrentLeft(
        targetLeft > rotateWidth ? rotateWidth : targetLeft
      );
    } else if (action.type === 'resizeRight') {
      $rotateEditApi.resizeRight({
        index: action.index,
        targetLeft,
        sceneId: loadedId,
      });
    } else if (action.type === 'resizeLeft') {
      $rotateEditApi.resizeLeft({
        index: action.index,
        targetLeft,
        sceneId: loadedId,
      });
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    const action = $rotateEditAction.getState();
    if (!action || !scrollRef.current || !containerRef.current) return;
    const { left: cLeft, right: cRight } =
      containerRef.current.getBoundingClientRect();
    mouseOffset.current = e.pageX - action.mouseLeft;
    if (e.pageX > cRight - 60) {
      setTrigger('right');
    } else if (e.pageX < cLeft + 60) {
      setTrigger('left');
    } else {
      setTrigger('none');
    }
    doAction();
  };

  const handleMouseUp = () => {
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
    setTrigger('none');
    itemActionRef.current = null;
    $rotateEditApi.setAction(null);
  };

  return (
    <div
      className="relative flex-1"
      ref={containerRef}
      onMouseDown={(e) => {
        if (!itemActionRef.current) return;
        $rotateEditApi.setAction({
          ...itemActionRef.current,
          mouseLeft: e.pageX,
          scrollBarLeft: scrollRef.current?.getScrollLeft() ?? 0,
        });
        document.addEventListener('mouseup', handleMouseUp);
        document.addEventListener('mousemove', handleMouseMove);
      }}
    >
      <Scrollbars
        thumbMinSize={60}
        renderTrackHorizontal={({ style, ...rest }: any) => {
          return <TrackHorizontal {...rest} style={{ ...style, height: 10 }} />;
        }}
        renderThumbHorizontal={(tProps: any) => <ThumbHorizontal {...tProps} />}
        ref={scrollRef}
        autoHide={false}
        onWheel={(e) => {
          if (!scrollRef.current) return;
          const y = scrollRef.current.getScrollLeft();
          scrollRef.current.scrollLeft(y + e.deltaY);
        }}
        onScrollFrame={() => {
          const action = $rotateEditAction.getState();
          if (!action) return;
          doAction();
        }}
      >
        <div
          onMouseDown={(e) => {
            // 如果有其它操作，不响应，防止轴标移动
            if (itemActionRef.current) return;
            const { left } = containerRef.current!.getBoundingClientRect();
            const scrollLeft = scrollRef.current?.getScrollLeft() ?? 0;
            const clickLeft = e.pageX - left + scrollLeft;
            $rotateEditApi.setCurrentLeft(
              clickLeft > rotateWidth ? rotateWidth : clickLeft
            );
          }}
        >
          <div className="flex h-40 border-b border-black-#34">
            {count.map((x) => (
              <RulerItem num={x} key={x} />
            ))}
          </div>
          <RorateList
            width={rotateWidth}
            onAction={({ actionType, left, index }) => {
              itemActionRef.current = {
                type: actionType,
                index,
                blockLeft: left,
              };
            }}
          />
          <RotateAudio width={rotateWidth} />
          <RotateSubtitle width={rotateWidth} />
          <Image
            src="/imgs/line.3d3704eabd8da289603456689411de92.png"
            type={ImageType.sysMedia}
            className="absolute top-0 z-50 cursor-pointer"
            style={{
              width: 13,
              height: 216,
              marginLeft: -6,
              left: currengLeft,
            }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={() => {
              itemActionRef.current = {
                type: 'moveLine',
                index: 0,
                blockLeft: $rotateCurrentLeft.getState(),
              };
            }}
          />
        </div>
      </Scrollbars>
    </div>
  );
}
